import { useState } from 'react'
import { Layout, Menu, Button, Avatar, Dropdown } from 'antd'
import {
  DashboardOutlined, DesktopOutlined, ShopOutlined, TeamOutlined,
  SettingOutlined, AuditOutlined, LogoutOutlined, MenuFoldOutlined, MenuUnfoldOutlined, UserOutlined,
} from '@ant-design/icons'
import { useNavigate, useLocation, Outlet } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { ROLE_LABELS } from '../auth'
import { ROLE_COLORS, LEDGER } from '../theme'
import { LedgerTag } from './ledger'

const { Header, Sider, Content } = Layout

const PAGE_TITLES = {
  '/dashboard': '数据看板',
  '/assets': '设备管理',
  '/assets/add': '新增登记',
  '/stores': '门店配置',
  '/staff': '员工配置',
  '/logs': '审计日志',
  '/settings': '账号设置',
}

function getPageTitle(pathname, canManageStore, canManageGlobally) {
  if (pathname.startsWith('/assets/edit/')) return '编辑设备'
  if (pathname === '/assets' && !canManageStore) return '外设状态'
  if (pathname === '/settings' && canManageGlobally) return '系统设置'
  return PAGE_TITLES[pathname] || '外设台账'
}

export default function AppLayout() {
  const [collapsed, setCollapsed] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const {
    user,
    isSuperAdmin,
    isStoreAdmin,
    canManageStore,
    canManageGlobally,
    signOut,
  } = useAuth()
  const displayName = user?.displayName?.trim() || user?.email?.split('@')[0] || '用户'
  const avatarLetter = displayName[0] || '用'
  const roleColor = ROLE_COLORS[user?.role] || ROLE_COLORS.staff

  const menuItems = [
    {
      type: 'group',
      label: '数据',
      children: [
        { key: '/dashboard', icon: <DashboardOutlined />, label: '数据看板' },
        {
          key: '/assets',
          icon: <DesktopOutlined />,
          label: canManageStore ? '设备管理' : '外设状态',
        },
      ],
    },
    ...(canManageGlobally
      ? [
          {
            type: 'group',
            label: '管理',
            children: [
              { key: '/stores', icon: <ShopOutlined />, label: '门店配置' },
              { key: '/staff', icon: <TeamOutlined />, label: '员工配置' },
              { key: '/logs', icon: <AuditOutlined />, label: '审计日志' },
              { key: '/settings', icon: <SettingOutlined />, label: '系统设置' },
            ],
          },
        ]
      : isStoreAdmin
        ? [
            {
              type: 'group',
              label: '本店管理',
              children: [
                { key: '/staff', icon: <TeamOutlined />, label: '员工配置' },
                { key: '/settings', icon: <SettingOutlined />, label: '账号设置' },
              ],
            },
          ]
      : []),
  ]

  const handleLogout = async () => {
    await signOut()
    navigate('/login')
  }

  const selectedKey = location.pathname.startsWith('/assets/edit') || location.pathname === '/assets/add'
    ? location.pathname.startsWith('/assets/edit') ? '/assets' : location.pathname
    : location.pathname

  const pageTitle = getPageTitle(location.pathname, canManageStore, canManageGlobally)

  return (
    <Layout style={{ minHeight: '100vh', background: LEDGER.paperDim }}>
      <Sider
        className="ledger-sider"
        trigger={null}
        collapsible
        collapsed={collapsed}
        breakpoint="lg"
        collapsedWidth={72}
        onBreakpoint={(broken) => { if (broken) setCollapsed(true) }}
        style={{
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 100,
          background: LEDGER.paper,
        }}
        width={200}
      >
        <div
          style={{
            height: 72,
            display: 'flex',
            flexDirection: 'column',
            alignItems: collapsed ? 'center' : 'flex-start',
            justifyContent: 'center',
            padding: collapsed ? '0 8px' : '0 16px',
            borderBottom: `2px solid ${LEDGER.line}`,
            color: LEDGER.ink,
          }}
        >
          <div style={{ fontSize: collapsed ? 13 : 16, fontWeight: 700, letterSpacing: '0.12em' }}>
            {collapsed ? '台账' : '外设台账'}
          </div>
          {!collapsed && (
            <span style={{ fontSize: 11, color: '#4a4a4a', marginTop: 4, letterSpacing: '0.04em' }}>
              序列号登记系统
            </span>
          )}
        </div>
        <Menu
          theme="light"
          mode="inline"
          className="ledger-menu"
          selectedKeys={[selectedKey]}
          items={menuItems}
          onClick={({ key }) => navigate(key)}
          style={{ paddingBottom: 96, borderInlineEnd: 'none', background: 'transparent' }}
        />
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            padding: '12px 14px',
            borderTop: `2px solid ${LEDGER.line}`,
            background: LEDGER.paper,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Avatar size="small" style={{ backgroundColor: roleColor, border: `1px solid ${LEDGER.line}` }}>
              {avatarLetter}
            </Avatar>
            {!collapsed && (
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {displayName}
                </div>
                <LedgerTag variant="accent" style={{ marginTop: 4 }}>
                  {ROLE_LABELS[user?.role] || user?.role}
                </LedgerTag>
              </div>
            )}
            <Button
              type="text"
              size="small"
              icon={<LogoutOutlined />}
              onClick={handleLogout}
              title="退出登录"
            />
          </div>
          {!collapsed && user?.hotel && !isSuperAdmin && (
            <div style={{ fontSize: 11, color: '#4a4a4a', marginTop: 8, paddingLeft: 40 }}>
              门店：{user.hotel}
            </div>
          )}
        </div>
      </Sider>
      <Layout
        style={{
          marginLeft: collapsed ? 72 : 200,
          transition: 'margin-left 0.2s',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          background: LEDGER.paperDim,
        }}
      >
        <Header
          className="ledger-header"
          style={{
            padding: '0 20px',
            background: LEDGER.paper,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
            height: 56,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
            />
            <span style={{ fontSize: 17, fontWeight: 600, letterSpacing: '0.04em' }}>{pageTitle}</span>
            {!isSuperAdmin && user?.hotel && (
              <LedgerTag variant="muted">{user.hotel}</LedgerTag>
            )}
          </div>
          <Dropdown menu={{ items: [{ key: 'logout', icon: <LogoutOutlined />, label: '退出登录', onClick: handleLogout }] }}>
            <Avatar
              icon={<UserOutlined />}
              style={{ cursor: 'pointer', backgroundColor: roleColor, border: `1px solid ${LEDGER.line}` }}
            />
          </Dropdown>
        </Header>
        <Content style={{ padding: 20, flex: 1, overflow: 'auto', background: LEDGER.paperDim }}>
          <div className="page-content app-layout-content">
            <Outlet />
          </div>
        </Content>
      </Layout>
    </Layout>
  )
}
